import React from 'react';
import { useTranslation } from 'react-i18next';
import { Row, Col, Typography, Form, Popover, Input, Spin, Card } from 'antd';
import PropTypes from 'prop-types';
import useLogWoodEvaluationCalculation from '../../hooks/useLogWoodEvaluationCalculation';
import { formatComma } from '../../utils/stringFormat';
import { Media } from "../../AppMedia";
import { defaultLogWoodPOWorkflowDetail } from '../../constance/dataTypes';

const { Title } = Typography;

const SummaryItem = ({ title, value, unit }) => {
  return (
    <div>
      <Media at="sm">
        <Row >
          <Col >
            <Popover content={unit} trigger="hover">
              <Form.Item
                label={title}
                colon={false}
              >
                <Input
                  readOnly
                  value={value != null ? formatComma(value) : ''}
                />
              </Form.Item>
            </Popover>
          </Col>
        </Row>
      </Media>
      <Media greaterThan="sm">
        <Row gutter={[16, 16]} valign="middle" style={{ width: '100%', textAlign: 'left' }}  >
          <Col span={9}>{title}</Col>
          <Col span={7}>
            <Input
              style={{ textAlign: 'right', marginBottom: 10 }}
              readOnly
              value={value != null ? formatComma(value) : ''}
            />
          </Col>
          <Col span={6}>{unit}</Col>

        </Row>
      </Media>
    </div>
  );
};

const LogWoodPOSummary = ({ detail, active }) => {
  const { t } = useTranslation();
  const updatedSummary = useLogWoodEvaluationCalculation({
    truckTransaction: detail.truck_transaction,
    logWoodEvaluationData: detail.log_wood_evaluation_data,
  });

  const isDataUpdated = () => {
    const logWoodEvaluationDataCreated = detail.log_wood_evaluation_data.created_by;
    const logWoodEvaluationDataUpdated = detail.log_wood_evaluation_data.updated_by;
    const isLogWoodEvaluationDataUpdated = logWoodEvaluationDataCreated !== logWoodEvaluationDataUpdated;
    const truckTransactionCreated = detail.truck_transaction.created_by;
    const truckTransactionUpdated = detail.truck_transaction.updated_by;
    const isTruckTransactionUpdated = truckTransactionCreated !== truckTransactionUpdated;
    return isLogWoodEvaluationDataUpdated || isTruckTransactionUpdated;
  };

  return (
    <Card>
      <Row>
        <Col span={12}>
          <Title level={5} style={{ textAlign: 'center', marginTop: 0 }}>
            {t('log_wood_po.evaluate_summary')}
          </Title>
          {[
            {
              title: t('log_wood_po.std_cost'),
              unit: `${t('units.baht')} / ${t('units.cubic')}${t('units.ft')}`,
              value: detail.log_summary.std_cost
            },
            {
              title: t('log_wood_po.exchange_std'),
              unit: `${t('units.cubic')}${t('units.ft')} / ${t('units.tonne')}`,
              value: detail.log_summary.exchange_std
            },
            {
              title: t('log_wood_evaluation_datas.std_buy_price'),
              unit: `${t('units.baht')} / ${t('units.kilo_gram')}`,
              value: detail.log_summary.std_buy_price
            },
            {
              title: t('log_wood_evaluation_datas.buy_price'),
              unit: `${t('units.baht')} / ${t('units.kilo_gram')}`,
              value: detail.log_summary.buy_price
            },
            {
              title: t('log_wood_po.real_cost'),
              unit: `${t('units.baht')} / ${t('units.kilo_gram')}`,
              value: detail.log_summary.real_cost
            },
            {
              title: t('log_wood_evaluation_datas.buy_amount'),
              unit: `${t('units.baht')}`,
              value: detail.log_summary.buy_amount
            },
            {
              title: t('log_wood_po.avg_weight'),
              unit: `${t('units.kilo_gram')} / ${t('units.log_wood')}`,
              value: detail.log_summary.avg_weight
            },
            {
              title: t('log_wood_evaluation_datas.cut_weight'),
              unit: `${t('units.kilo_gram')}`,
              value: detail.log_summary.cut_weight
            },
            {
              title: t('log_wood_evaluation_datas.return_wood'),
              unit: `${t('units.log_wood')}`,
              value: detail.log_summary.return_wood
            },
            {
              title: t('log_wood_po.net_weight'),
              unit: `${t('units.kilo_gram')}`,
              value: detail.log_summary.net_weight
            },
          ].map((item, index) => (
            <SummaryItem key={index} title={item.title} unit={item.unit} value={item.value} />
          ))}
        </Col>

        {/* Updated Summary Section */}
        <Col span={12}>
          <Title level={5} style={{ textAlign: 'center', marginTop: 0 }}>
            {t('log_wood_po.updated_summary')}
          </Title>
          {isDataUpdated() &&
            [
              {
                title: t('log_wood_po.std_cost'),
                unit: `${t('units.baht')} / ${t('units.cubic')}${t('units.ft')}`,
                value: updatedSummary.std_cost
              },
              {
                title: t('log_wood_po.exchange_std'),
                unit: `${t('units.cubic')}${t('units.ft')} / ${t('units.tonne')}`,
                value: updatedSummary.exchange_std
              },
              {
                title: t('log_wood_evaluation_datas.std_buy_price'),
                unit: `${t('units.baht')} / ${t('units.kilo_gram')}`,
                value: updatedSummary.std_buy_price
              },
              {
                title: t('log_wood_evaluation_datas.buy_price'),
                unit: `${t('units.baht')} / ${t('units.kilo_gram')}`,
                value: updatedSummary.buy_price
              },
              {
                title: t('log_wood_po.real_cost'),
                unit: `${t('units.baht')} / ${t('units.kilo_gram')}`,
                value: updatedSummary.real_cost
              },
              {
                title: t('log_wood_evaluation_datas.buy_amount'),
                unit: `${t('units.baht')}`,
                value: updatedSummary.buy_amount
              },
              {
                title: t('log_wood_po.avg_weight'),
                unit: `${t('units.kilo_gram')} / ${t('units.log_wood')}`,
                value: updatedSummary.avg_weight
              },
              {
                title: t('log_wood_evaluation_datas.cut_weight'),
                unit: `${t('units.kilo_gram')}`,
                value: updatedSummary.cut_weight
              },
              {
                title: t('log_wood_evaluation_datas.return_wood'),
                unit: `${t('units.log_wood')}`,
                value: updatedSummary.return_wood
              },
              {
                title: t('log_wood_po.net_weight'),
                unit: `${t('units.kilo_gram')}`,
                value: updatedSummary.net_weight
              },
            ].map((item, index) => (
              <SummaryItem key={index} title={item.title} unit={item.unit} value={item.value} />
            ))}
        </Col>
      </Row>
    </Card>
  )
};


export default React.memo(LogWoodPOSummary);


LogWoodPOSummary.defaultProps = {
  updatedDetail: defaultLogWoodPOWorkflowDetail,
  active: false,
};

LogWoodPOSummary.propTypes = {
  updatedDetail: PropTypes.object,
  active: PropTypes.bool,
};
